/**
 * OmologaOrdineCard — riquadro "Omologa associata" nel dettaglio richiesta.
 * Mostra i metadati dell'omologa associata all'ordine (r.omologa) + badge
 * stato/stato_firma + anteprima scheda + download firmato.
 *
 * Modello Z (TD-2): la SCHEDA resta ordine-specifica → anteprima dall'endpoint
 * d'ordine /api/richieste/{id}/scheda-omologa-pdf. Il FIRMATO è fonte unica
 * sull'omologa (omologhe.pdf_firmato_url) → si scarica dall'endpoint omologa
 * /api/omologhe/{id}/firmato-pdf (NON più da documents/DocumentiCard).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSignature, ExternalLink, Eye } from 'lucide-react';
import type { OmologaInline } from '../../types/omologa';
import { openPdfInTab } from '../../utils/pdfPreview';
import { apriFirmatoPdfOmologa } from '../../api/omologhe';
import i18n from '../../i18n';
import Badge from '../ui/Badge';

interface Props {
  richiestaId: string;
  omologa: OmologaInline;
}

function statoBadge(stato: OmologaInline['stato']) {
  if (stato === 'valida') return <Badge variant="success">{i18n.t('conferimenti.omologa_card.stato_valida')}</Badge>;
  if (stato === 'scaduta') return <Badge variant="warning">{i18n.t('conferimenti.omologa_card.stato_scaduta')}</Badge>;
  return <Badge variant="neutral">{i18n.t('conferimenti.omologa_card.stato_indeterminata')}</Badge>;
}

function statoFirmaBadge(s: OmologaInline['stato_firma']) {
  const variant =
    s === 'firmata' ? 'success' : s === 'inviata' ? 'purple' : s === 'generata' ? 'info' : 'neutral';
  return <Badge variant={variant}>{s}</Badge>;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1 text-xs">
      <span className="text-ink-soft">{label}</span>
      <span className="text-ink-dark text-right">{value}</span>
    </div>
  );
}

export default function OmologaOrdineCard({ richiestaId, omologa }: Props) {
  const { t } = useTranslation();
  const [err, setErr] = useState<string | null>(null);

  async function anteprimaScheda() {
    try {
      await openPdfInTab(`/api/richieste/${richiestaId}/scheda-omologa-pdf`);
    } catch {
      setErr(t('conferimenti.omologa_card.scheda_non_disponibile'));
    }
  }

  async function anteprimaFirmato() {
    try {
      await apriFirmatoPdfOmologa(omologa.id);   // endpoint inline → anteprima in nuova tab
    } catch {
      setErr(t('conferimenti.omologa_card.firmato_non_disponibile'));
    }
  }

  return (
    <div className="bg-white border border-stone-mid rounded-xl overflow-hidden" data-testid="card-omologa-ordine">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-dark">
          <FileSignature size={15} className="text-ink-soft" />
          {t('conferimenti.omologa_card.titolo')}
        </div>
        <div className="flex items-center gap-1.5">
          {statoBadge(omologa.stato)}
          {statoFirmaBadge(omologa.stato_firma)}
        </div>
      </div>

      <div className="px-5 py-4">
        <Row label={t('conferimenti.omologa_card.numero')} value={omologa.numero_omologa || '—'} />
        <Row label={t('conferimenti.omologa_card.conferimenti')} value={omologa.numero_conferimenti ?? '—'} />
        <Row label={t('conferimenti.omologa_card.frequenza')} value={omologa.frequenza || '—'} />
        <Row label={t('conferimenti.omologa_card.pezzatura')} value={omologa.pezzatura || '—'} />
        <Row label={t('conferimenti.omologa_card.data_firma')} value={omologa.data_firma || '—'} />
        <Row label={t('conferimenti.omologa_card.scadenza')} value={omologa.data_scadenza || '—'} />
        <Row label={t('conferimenti.omologa_card.trasportatori')} value={omologa.trasportatori.length} />
        <Row label={t('conferimenti.omologa_card.intermediari')} value={omologa.intermediari.length} />

        <button
          type="button"
          onClick={anteprimaScheda}
          className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
        >
          <ExternalLink size={14} /> {t('conferimenti.omologa_card.anteprima_scheda')}
        </button>

        {omologa.pdf_firmato_url && (
          <button
            type="button"
            onClick={anteprimaFirmato}
            data-testid="btn-scarica-firmato-ordine"
            className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors"
          >
            <Eye size={14} /> {t('conferimenti.omologa_card.anteprima_firmata')}
          </button>
        )}

        {err && <p className="mt-2 text-xs text-accent-danger">{err}</p>}
      </div>
    </div>
  );
}
